<template>
  <div class="chat-tool-result">
    <header class="chat-tool-result__header">
      <div class="chat-tool-result__title">
        <BaseIcon :name="meta.icon" size="12" />
        <span>{{ meta.label }}</span>
        <span v-if="countLabel" class="chat-tool-result__count">{{ countLabel }}</span>
      </div>
      <router-link
        v-if="meta.viewAll"
        :to="meta.viewAll.to"
        class="chat-tool-result__view-all"
      >
        {{ meta.viewAll.label }}
        <BaseIcon name="chevron" size="10" :rotate="-90" />
      </router-link>
    </header>

    <div v-if="isTruncated" class="chat-tool-result__truncated">
      <BaseIcon name="info" size="12" />
      <span>{{ $t('home.chat.tool_result.truncated') }}</span>
    </div>

    <!-- Stats tools render KPI tiles -->
    <div v-else-if="isStats" class="chat-tool-result__stats">
      <StatTile
        v-for="tile in statTiles"
        :key="tile.label"
        :label="tile.label"
        :value="tile.value"
        :sublabel="tile.sublabel"
        :icon="tile.icon"
        :tone="tile.tone"
      />
      <div v-if="statsContext" class="chat-tool-result__stats-context">
        {{ statsContext }}
      </div>
    </div>

    <!-- List tools render shared list items -->
    <div v-else-if="items.length" class="chat-tool-result__body">
      <component
        :is="meta.itemComponent"
        v-for="(item, index) in displayedItems"
        :key="item.id || index"
        v-bind="{ [meta.itemProp]: item }"
      />
      <button
        v-if="hasMore"
        type="button"
        class="chat-tool-result__more"
        @click="expanded = true"
      >
        {{ $t('home.chat.tool_result.show_all', { n: items.length - DEFAULT_LIMIT }) }}
      </button>
    </div>

    <div v-else class="chat-tool-result__empty">
      <BaseIcon :name="meta.icon" size="14" />
      <span>{{ meta.emptyLabel || $t('home.chat.tool_result.empty') }}</span>
    </div>
  </div>
</template>

<script>
  import { computed, ref, getCurrentInstance, defineAsyncComponent } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import StatTile from '@/components/partials/StatTile.vue';
  import { formatCurrency } from '@/utils/currency';

  const InvoiceListItem = defineAsyncComponent(
    () => import('@/components/partials/lists/InvoiceListItem.vue')
  );
  const TaskListItem = defineAsyncComponent(
    () => import('@/components/partials/lists/TaskListItem.vue')
  );
  const ProposalListItem = defineAsyncComponent(
    () => import('@/components/partials/lists/ProposalListItem.vue')
  );
  const AppointmentListItem = defineAsyncComponent(
    () => import('@/components/partials/lists/AppointmentListItem.vue')
  );
  const SubmissionListItem = defineAsyncComponent(
    () => import('@/components/partials/lists/SubmissionListItem.vue')
  );
  const LeadListItem = defineAsyncComponent(
    () => import('@/components/partials/lists/LeadListItem.vue')
  );
  const DealListItem = defineAsyncComponent(
    () => import('@/components/partials/lists/DealListItem.vue')
  );
  const WorkOrderListItem = defineAsyncComponent(
    () => import('@/components/partials/lists/WorkOrderListItem.vue')
  );

  const DEFAULT_LIMIT = 5;

  /**
   * Per-tool metadata: how to render each `tool_results[]` entry.
   * Keep this in sync with the read-tool list in AI_CHAT_WORKSPACE.md.
   *
   * Each entry returns:
   *   - icon, labelKey, emptyLabelKey, viewAll route
   *   - itemsKey: where to find the list inside `data`
   *   - itemComponent / itemProp: what to render per item (or `null` for stats tools)
   */
  const TOOL_META = {
    get_outstanding_invoices: {
      icon: 'file-text',
      labelKey: 'home.chat.tool_result.invoices.label',
      emptyLabelKey: 'home.chat.tool_result.invoices.empty',
      viewAll: { to: '/invoices?status=open', labelKey: 'home.chat.tool_result.invoices.view_all' },
      itemsKey: 'invoices',
      itemComponent: InvoiceListItem,
      itemProp: 'invoice',
    },
    get_open_tasks: {
      icon: 'check-square',
      labelKey: 'home.chat.tool_result.tasks.label',
      emptyLabelKey: 'home.chat.tool_result.tasks.empty',
      viewAll: { to: '/tasks', labelKey: 'home.chat.tool_result.tasks.view_all' },
      itemsKey: 'tasks',
      itemComponent: TaskListItem,
      itemProp: 'task',
    },
    get_overdue_tasks: {
      icon: 'clock',
      labelKey: 'home.chat.tool_result.overdue_tasks.label',
      emptyLabelKey: 'home.chat.tool_result.tasks.empty',
      viewAll: { to: '/tasks', labelKey: 'home.chat.tool_result.tasks.view_all' },
      itemsKey: 'tasks',
      itemComponent: TaskListItem,
      itemProp: 'task',
    },
    get_recent_submissions: {
      icon: 'paper-plane',
      labelKey: 'home.chat.tool_result.submissions.label',
      emptyLabelKey: 'home.chat.tool_result.submissions.empty',
      viewAll: {
        to: '/submissions',
        labelKey: 'home.chat.tool_result.submissions.view_all',
      },
      itemsKey: 'submissions',
      itemComponent: SubmissionListItem,
      itemProp: 'submission',
    },
    get_open_proposals: {
      icon: 'file',
      labelKey: 'home.chat.tool_result.proposals.label',
      emptyLabelKey: 'home.chat.tool_result.proposals.empty',
      viewAll: { to: '/proposals', labelKey: 'home.chat.tool_result.proposals.view_all' },
      itemsKey: 'proposals',
      itemComponent: ProposalListItem,
      itemProp: 'proposal',
    },
    get_upcoming_appointments: {
      icon: 'calendar',
      labelKey: 'home.chat.tool_result.appointments.label',
      emptyLabelKey: 'home.chat.tool_result.appointments.empty',
      viewAll: {
        to: '/appointments',
        labelKey: 'home.chat.tool_result.appointments.view_all',
      },
      itemsKey: 'appointments',
      itemComponent: AppointmentListItem,
      itemProp: 'appointment',
    },
    search_leads: {
      icon: 'users',
      labelKey: 'home.chat.tool_result.leads.label',
      emptyLabelKey: 'home.chat.tool_result.leads.empty',
      viewAll: { to: '/crm', labelKey: 'home.chat.tool_result.leads.view_all' },
      itemsKey: 'leads',
      itemComponent: LeadListItem,
      itemProp: 'lead',
    },
    search_deals: {
      icon: 'handshake',
      labelKey: 'home.chat.tool_result.deals.label',
      emptyLabelKey: 'home.chat.tool_result.deals.empty',
      viewAll: { to: '/deals', labelKey: 'home.chat.tool_result.deals.view_all' },
      itemsKey: 'deals',
      itemComponent: DealListItem,
      itemProp: 'deal',
    },
    get_invoice_stats: {
      icon: 'euro',
      labelKey: 'home.chat.tool_result.invoice_stats.label',
      isStats: true,
    },
    get_deal_stats: {
      icon: 'activity',
      labelKey: 'home.chat.tool_result.deal_stats.label',
      isStats: true,
    },
  };

  export default {
    name: 'ChatToolResult',

    components: { BaseIcon, StatTile },

    props: {
      result: { type: Object, required: true },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const expanded = ref(false);

      const meta = computed(() => {
        const config = TOOL_META[props.result.tool];
        if (!config) {
          return {
            icon: 'info',
            label: props.result.tool || $t('home.chat.tool_result.unknown'),
            isStats: false,
          };
        }
        return {
          icon: config.icon,
          label: $t(config.labelKey),
          emptyLabel: config.emptyLabelKey ? $t(config.emptyLabelKey) : '',
          viewAll: config.viewAll
            ? { to: config.viewAll.to, label: $t(config.viewAll.labelKey) }
            : null,
          itemsKey: config.itemsKey,
          itemComponent: config.itemComponent,
          itemProp: config.itemProp,
          isStats: Boolean(config.isStats),
        };
      });

      const data = computed(() => props.result?.data || {});

      const isTruncated = computed(() => data.value._truncated === true);

      const isStats = computed(() => meta.value.isStats === true);

      const items = computed(() => {
        if (isStats.value || isTruncated.value) return [];
        const key = meta.value.itemsKey;
        const list = key && Array.isArray(data.value[key]) ? data.value[key] : [];
        return list;
      });

      const total = computed(() => {
        const t = data.value.total ?? data.value.count ?? null;
        if (typeof t === 'number') return t;
        return items.value.length || null;
      });

      const countLabel = computed(() => {
        if (isStats.value || isTruncated.value) return '';
        const t = total.value;
        if (t === null) return '';
        return `${t}`;
      });

      const displayedItems = computed(() =>
        expanded.value ? items.value : items.value.slice(0, DEFAULT_LIMIT)
      );

      const hasMore = computed(
        () => !expanded.value && items.value.length > DEFAULT_LIMIT
      );

      // ---- stats helpers ----

      const statTiles = computed(() => {
        if (props.result.tool === 'get_invoice_stats') return invoiceStatTiles.value;
        if (props.result.tool === 'get_deal_stats') return dealStatTiles.value;
        return [];
      });

      const statsContext = computed(() => {
        const period = data.value.period;
        const from = data.value.from_date;
        const to = data.value.to_date;
        if (!period && !from) return '';
        if (from && to) {
          return $t('home.chat.tool_result.stats_range', { from, to });
        }
        return period || '';
      });

      const invoiceStatTiles = computed(() => {
        const s = data.value.stats || {};
        return [
          {
            label: $t('home.chat.tool_result.invoice_stats.invoiced'),
            value: formatCurrency(s.total_invoiced ?? 0),
            sublabel: s.invoice_count != null ? `${s.invoice_count} facturen` : '',
            icon: 'file-text',
            tone: 'brand',
          },
          {
            label: $t('home.chat.tool_result.invoice_stats.paid'),
            value: formatCurrency(s.total_paid ?? 0),
            icon: 'check-circle',
            tone: 'green',
          },
          {
            label: $t('home.chat.tool_result.invoice_stats.outstanding'),
            value: formatCurrency(s.total_outstanding ?? 0),
            sublabel:
              s.outstanding_count != null
                ? `${s.outstanding_count} ${$t('home.chat.tool_result.invoice_stats.open')}`
                : '',
            icon: 'clock',
            tone: 'amber',
          },
        ];
      });

      const dealStatTiles = computed(() => {
        const s = data.value.stats || {};
        const tiles = [];
        if (s.total_deals != null) {
          tiles.push({
            label: $t('home.chat.tool_result.deal_stats.total'),
            value: String(s.total_deals),
            icon: 'handshake',
            tone: 'brand',
          });
        }
        if (s.won_value != null || s.total_won != null) {
          tiles.push({
            label: $t('home.chat.tool_result.deal_stats.won'),
            value: formatCurrency(s.won_value ?? s.total_won ?? 0),
            sublabel: s.won_count != null ? `${s.won_count} deals` : '',
            icon: 'check-circle',
            tone: 'green',
          });
        }
        if (s.open_value != null || s.total_open != null) {
          tiles.push({
            label: $t('home.chat.tool_result.deal_stats.pipeline'),
            value: formatCurrency(s.open_value ?? s.total_open ?? 0),
            sublabel: s.open_count != null ? `${s.open_count} ${$t('home.chat.tool_result.deal_stats.open')}` : '',
            icon: 'activity',
            tone: 'amber',
          });
        }
        return tiles;
      });

      return {
        DEFAULT_LIMIT,
        meta,
        isTruncated,
        isStats,
        items,
        displayedItems,
        hasMore,
        countLabel,
        expanded,
        statTiles,
        statsContext,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .chat-tool-result {
    margin-top: 0.625rem;
    border: 1px solid var(--slate-200);
    border-radius: 0.625rem;
    background: white;
    overflow: hidden;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      background: var(--slate-50);
      border-bottom: 1px solid var(--slate-200);
    }

    &__title {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-700);
      text-transform: uppercase;
      letter-spacing: 0.03em;

      .icon {
        color: var(--slate-500);
      }
    }

    &__count {
      display: inline-flex;
      align-items: center;
      padding: 1px 6px;
      border-radius: 9999px;
      background: var(--slate-200);
      color: var(--slate-700);
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0;
      text-transform: none;
    }

    &__view-all {
      display: inline-flex;
      align-items: center;
      gap: 0.125rem;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--brand-700);
      text-decoration: none;
      padding: 2px 4px;
      border-radius: 0.25rem;

      @include hover {
        background: var(--brand-50);
        color: var(--brand-800);
      }
    }

    &__body {
      display: flex;
      flex-direction: column;
    }

    &__more {
      display: block;
      width: 100%;
      padding: 0.625rem 1rem;
      background: white;
      border: none;
      border-top: 1px solid var(--slate-100);
      color: var(--brand-700);
      font-size: 0.8125rem;
      font-weight: 500;
      cursor: pointer;
      text-align: center;

      @include hover {
        background: var(--brand-50);
      }
    }

    &__empty,
    &__truncated {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.875rem 1rem;
      font-size: 0.8125rem;
      color: var(--slate-500);

      .icon {
        color: var(--slate-400);
      }
    }

    &__truncated {
      background: var(--amber-50);
      color: var(--amber-700);

      .icon {
        color: var(--amber-600);
      }
    }

    &__stats {
      display: grid;
      gap: 0.5rem;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      padding: 0.75rem;
    }

    &__stats-context {
      grid-column: 1 / -1;
      font-size: 0.75rem;
      color: var(--slate-500);
      padding-top: 0.25rem;
    }
  }
</style>
